import type { ThunkAction, ThunkDispatch } from "@reduxjs/toolkit";

import type { ReduxState } from "@dashboard/types/reduxState";
import { ResolvedEdiblesState } from "@dashboard/types/resolvedEdibles";
import type { ThunkExtraArgument } from "@dashboard/types/thunk";

import type { ErrorReducerAction } from "../reducers/errorReducer";
import { ResolvedEdiblesReducerAction } from "../reducers/resolvedEdiblesReducer";
import { addError, addUserFacingError } from "./addErrorAction";

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  ResolvedEdiblesReducerAction
>;

type ResolvedEdiblesThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  ResolvedEdiblesReducerAction | ErrorReducerAction
>;

export const fetchResolvedEdibles: () => ThunkActionReturn = () => {
  return (
    dispatch: ResolvedEdiblesThunkDispatch,
    getState: () => ReduxState,
    { resolvedEdiblesApi }: ThunkExtraArgument
  ) => {
    // get current restaurant from state. this allows for running fetchDishes
    // as a refresh operation even when restaurants are changing.
    const currentRest = getState().restaurant.selectedRestaurant;
    if (!currentRest) {
      return Promise.reject("No restaurant selected");
    }

    return resolvedEdiblesApi
      .fetchResolvedEdibles(currentRest.uuid)
      .then((resolvedEdibleResponse) => {
        const resolvedEdibleState: ResolvedEdiblesState = {
          resolvedDishesById: resolvedEdibleResponse.dishes,
          resolvedProductsById: resolvedEdibleResponse.products,
        };

        // If there are user facing errors returned by the API, we should show them
        // to the user.
        if (resolvedEdibleResponse.resolutionErrors.length > 0) {
          resolvedEdibleResponse.resolutionErrors.forEach(
            addUserFacingError(dispatch)
          );
        }

        dispatch({
          type: "FETCHED_RESOLVED_EDIBLES",
          payload: resolvedEdibleState,
        });
      })
      .catch(addError(dispatch));
  };
};
